import { redactForDisplay, redactedJson } from '../lib/redaction';

export type RuntimeStatusTone = 'green' | 'blue' | 'yellow' | 'red' | 'muted';

export type RuntimeStatusItem = {
  label: string;
  value: string;
  evidence: string;
  tone?: RuntimeStatusTone;
};

export type RuntimeEvidenceSources = {
  engagements?: unknown;
  sessions?: unknown;
  audit?: unknown;
  errors?: Record<string, string>;
};

export type RuntimeEngagementDetailSources = {
  engagement?: unknown;
  relations?: unknown;
  state?: unknown;
  events?: unknown;
  flow?: unknown;
  loopVerdict?: unknown;
  errors?: Record<string, string>;
};

export type RuntimeSessionDetailSources = {
  session?: unknown;
  errors?: Record<string, string>;
};

export type RuntimeAuditDetailSources = {
  eventId: string;
  audit?: unknown;
  errors?: Record<string, string>;
};

export type RuntimeHealth = {
  key: string;
  label: string;
  tone: RuntimeStatusTone;
};

export type RuntimeEngagementRow = {
  id: string;
  personaName: string;
  platform: string;
  sessionKey: string;
  state: string;
  paused: boolean;
  sessionsCount: number;
  liveSessions: number;
  createdAt: string;
  updatedAt: string;
  health: RuntimeHealth;
  detailHref: string;
};

export type RuntimeRelatedEngagementRow = {
  id: string;
  relation: string;
  personaName: string;
  platform: string;
  sessionKey: string;
  state: string;
  detailHref: string;
};

export type RuntimeRelationshipGroup = {
  label: string;
  rows: RuntimeRelatedEngagementRow[];
};

export type RuntimeActionStatus = {
  action: string;
  allowed: boolean;
  code: string;
  message: string;
  currentState: string;
  targetState: string;
  currentAxes: Array<[string, string]>;
  targetAxes: Array<[string, string]>;
  requiredFields: string[];
  optionalFields: string[];
  confirmationRequired: boolean;
  wouldRecordEvent: boolean;
};

export type RuntimeEventField = {
  key: string;
  value: string;
};

export type RuntimeArtifactRef = {
  kind: string;
  ref: string;
};

export type RuntimeEventRow = {
  id: string;
  type: string;
  timestamp: string;
  actor: string;
  personaId: string;
  workflowId: string;
  round: string;
  fromPersona: string;
  toPersona: string;
  intent: string;
  result: string;
  parentEventId: string;
  engagementId: string;
  sessionId: string;
  stateChange: string;
  payloadFields: RuntimeEventField[];
  artifactRefs: RuntimeArtifactRef[];
  rawJson: string;
};

export type RuntimeLoopVerdictGate = {
  label: string;
  status: string;
  reason: string;
  eventId: string;
  tone: RuntimeStatusTone;
};

export type RuntimeLoopVerdictSummary = {
  available: boolean;
  overall: string;
  overallTone: RuntimeStatusTone;
  workflowId: string;
  issueId: string;
  currentRound: string;
  gates: RuntimeLoopVerdictGate[];
  missingEvents: string[];
  invalidEvents: string[];
  fallbackUsed: boolean;
  manualMirrorDetected: boolean;
  nextAction: string;
  rawJson: string;
};

export type RuntimeEngagementDetailModel = {
  engagement: RuntimeEngagementRow;
  status: RuntimeStatusItem[];
  loopVerdict: RuntimeLoopVerdictSummary;
  relationshipGroups: RuntimeRelationshipGroup[];
  axesRows: Array<[string, string]>;
  allowedActions: RuntimeActionStatus[];
  blockedActions: RuntimeActionStatus[];
  timeline: RuntimeEventRow[];
  stateMachine: Array<[string, string]>;
  exportFilters: Array<[string, string]>;
  rawJson: string;
  partialNotices: string[];
  contractGaps: string[];
};

export type RuntimeSessionRow = {
  id: string;
  name: string;
  personaName: string;
  platform: string;
  sessionKey: string;
  active: boolean;
  live: boolean;
  historyCount: number;
  runtimeProfileId: string;
  runtimeStatus: string;
  providerModel: string;
  threadId: string;
  agentSessionId: string;
  personaSnapshotId: string;
  platformId: string;
  promptArtifactCount: number;
  // Which channel carried the runtime contract into the agent for this
  // session: developer_instructions, system_prompt, or none. It is the reason
  // the two fields below may legitimately be empty, so it has to be readable
  // next to them — an adapter with no developer-instructions channel is a fact
  // to display, not a gap to flag.
  promptInjectionStrategy: string;
  developerInstructionsHash: string;
  developerPromptArtifact: string;
  // The one sentence the page shows in place of a hash. Empty when a hash is
  // present and nothing needs explaining.
  promptEvidenceNote: string;
  detailHref: string;
  profileHref: string;
  partialWarnings: string[];
};

export type RuntimeAuditRow = {
  id: string;
  timestamp: string;
  actor: string;
  action: string;
  resourceKind: string;
  resourceId: string;
  resourceName: string;
  resourceLabel: string;
  detailHref: string;
};

export type RuntimePromptArtifactRow = {
  artifactId: string;
  name: string;
  version: number;
  hash: string;
};

export type RuntimeHistoryRow = {
  role: string;
  timestamp: string;
  contentPreview: string;
};

export type RuntimeSessionDetailModel = {
  session: RuntimeSessionRow;
  relationships: Array<[string, string]>;
  historyRows: RuntimeHistoryRow[];
  promptModules: Array<Record<string, unknown>>;
  rawJson: string;
  partialNotices: string[];
};

export type RuntimeProfileEvidenceModel = {
  session: RuntimeSessionRow;
  profileId: string;
  relationships: Array<[string, string]>;
  promptArtifacts: RuntimePromptArtifactRow[];
  rawJson: string;
  emptyState: { title: string; description: string } | null;
  partialNotices: string[];
};

export type RuntimeAuditDetailModel = {
  event: RuntimeAuditRow | null;
  rawJson: string;
  emptyState: { title: string; description: string } | null;
  partialNotices: string[];
};

export type RuntimeEvidenceModel = {
  header: {
    title: string;
    description: string;
    meta: Array<[string, string]>;
  };
  status: RuntimeStatusItem[];
  engagements: RuntimeEngagementRow[];
  sessions: RuntimeSessionRow[];
  auditRows: RuntimeAuditRow[];
  emptyState: { title: string; description: string } | null;
  partialNotices: string[];
  rawJson: string;
  contractGaps: string[];
};

export function buildRuntimeEvidenceModel(sources: RuntimeEvidenceSources): RuntimeEvidenceModel {
  const engagements = getArray(asRecord(sources.engagements).engagements).map(engagementRow);
  const sessions = getArray(asRecord(sources.sessions).sessions).map(sessionRow);
  const auditRows = getArray(asRecord(sources.audit).events).map(auditRow);
  const activeEngagements = engagements.filter(engagement => engagement.health.key === 'active').length;
  const pausedEngagements = engagements.filter(engagement => engagement.paused).length;
  const profiledCount = sessions.filter(session => session.runtimeProfileId).length;
  const behindCount = sessions.filter(session => session.runtimeStatus === 'behind').length;

  return {
    header: {
      title: 'Runtime Operations',
      description: 'Operational evidence and controls for engagements, sessions, state machine projections, live timelines, exports, audit, and controlled test messages.',
      meta: [
        ['Kind', 'Runtime evidence + controls'],
        ['Source', 'Management API'],
        ['Engagements', '/api/v1/engagements'],
        ['Events', '/api/v1/events + SSE'],
      ],
    },
    status: [
      { label: 'Engagements', value: String(engagements.length), evidence: 'Live /api/v1/engagements rows', tone: engagements.length ? 'green' : 'muted' },
      { label: 'Active / paused', value: `${activeEngagements} / ${pausedEngagements}`, evidence: 'Health badges from state + live sessions', tone: pausedEngagements ? 'yellow' : activeEngagements ? 'blue' : 'muted' },
      { label: 'Sessions', value: `${sessions.length} (${profiledCount} profiled)`, evidence: 'SessionRuntimeProfile-capable /api/v1/sessions', tone: sessions.length ? 'green' : 'muted' },
      { label: 'Audit rows', value: String(auditRows.length), evidence: behindCount ? `${behindCount} sessions behind current prompt evidence` : 'Control-plane audit events', tone: behindCount ? 'yellow' : auditRows.length ? 'blue' : 'muted' },
    ],
    engagements,
    sessions,
    auditRows,
    emptyState: engagements.length || sessions.length ? null : {
      title: 'No runtime rows returned.',
      description: 'Runtime operations appear after engagements or sessions exist. Contract gaps are still shown so unsupported capabilities remain explicit.',
    },
    partialNotices: partialNotices(sources.errors),
    rawJson: redactedJson(sources),
    contractGaps: runtimeContractGaps(),
  };
}

export function buildRuntimeEngagementDetailModel(sources: RuntimeEngagementDetailSources): RuntimeEngagementDetailModel {
  const engagement = engagementRow(sources.engagement);
  const stateRecord = asRecord(sources.state);
  const flowRecord = asRecord(sources.flow);
  const projection = asRecord(stateRecord.projection);
  const effectiveState = stringValue(stateRecord.state) || stringValue(stateRecord.canonical_state) || engagement.state;
  const effectivePaused = typeof stateRecord.paused === 'boolean' ? boolValue(stateRecord.paused) : engagement.paused;
  const health = deriveEngagementHealth({ ...engagement, state: effectiveState, paused: effectivePaused });
  const timeline = getArray(asRecord(sources.events).events).map(eventRow);
  const axesRows = axesTableRows(asRecord(stateRecord.axes || (asRecord(sources.engagement).axes)));
  const loopVerdict = loopVerdictSummary(sources.loopVerdict);

  return {
    engagement: { ...engagement, state: effectiveState, paused: effectivePaused, health },
    status: [
      { label: 'State', value: effectiveState || 'Unknown', evidence: 'GET /api/v1/engagements/{id}/state', tone: health.tone },
      { label: 'Health', value: health.label, evidence: 'Derived from state, paused flag, live sessions', tone: health.tone },
      { label: 'Loop Verdict', value: loopVerdict.overall || 'Unavailable', evidence: loopVerdict.available ? '/api/v1/loop_verdicts' : 'No workflow contract verdict returned', tone: loopVerdict.available ? loopVerdict.overallTone : 'muted' },
      { label: 'Live sessions', value: String(engagement.liveSessions), evidence: 'engagement.live_sessions', tone: engagement.liveSessions ? 'green' : 'muted' },
      { label: 'Projection', value: stringValue(projection.source) || 'Unavailable', evidence: stringValue(projection.version) || 'No projection version', tone: projection.source ? 'blue' : 'yellow' },
    ],
    loopVerdict,
    relationshipGroups: relationshipGroups(sources.relations),
    axesRows,
    allowedActions: getArray(stateRecord.allowed_actions).map(actionStatus),
    blockedActions: getArray(stateRecord.blocked_actions).map(actionStatus),
    timeline,
    stateMachine: [
      ['Flow', stringValue(flowRecord.id) || stringValue(stateRecord.flow_id) || 'engagement'],
      ['Projection source', stringValue(projection.source) || 'contract gap: projection unavailable'],
      ['Projection version', stringValue(stateRecord.projection_version) || stringValue(projection.version) || '0'],
      ['Transitions', String(getArray(flowRecord.transitions).length)],
      ['Actions', getArray(flowRecord.actions).map(item => stringValue(asRecord(item).id)).filter(Boolean).join(', ') || 'None returned'],
      ['Event log source of truth', boolValue(flowRecord.event_log_source_of_truth) ? 'Yes' : 'Unknown'],
    ],
    exportFilters: [['engagement_id', engagement.id]],
    rawJson: redactedJson(sources),
    partialNotices: partialNotices(sources.errors),
    contractGaps: runtimeContractGaps(),
  };
}

export function buildRuntimeSessionDetailModel(sources: RuntimeSessionDetailSources): RuntimeSessionDetailModel {
  const sessionRecord = asRecord(sources.session);
  const session = sessionRow(sessionRecord);
  const profile = asRecord(sessionRecord.runtime_profile);
  const relationships = relationshipRows(sessionRecord, profile);
  const historyRows = getArray(sessionRecord.history).map(historyRow);
  const promptModules = getArray(sessionRecord.prompt_modules).map(item => redactForDisplay(asRecord(item)) as Record<string, unknown>);

  return {
    session,
    relationships,
    historyRows,
    promptModules,
    rawJson: redactedJson(sources),
    partialNotices: [...partialNotices(sources.errors), ...session.partialWarnings],
  };
}

export function buildRuntimeProfileEvidenceModel(sources: RuntimeSessionDetailSources): RuntimeProfileEvidenceModel {
  const sessionRecord = asRecord(sources.session);
  const session = sessionRow(sessionRecord);
  const profile = asRecord(sessionRecord.runtime_profile);
  const promptArtifacts = getArray(profile.prompt_artifacts).map(promptArtifactRow);

  return {
    session,
    profileId: session.runtimeProfileId,
    relationships: relationshipRows(sessionRecord, profile),
    promptArtifacts,
    rawJson: redactedJson({ runtime_profile: profile }),
    emptyState: session.runtimeProfileId ? null : {
      title: 'Runtime profile evidence unavailable.',
      description: 'The session API did not return a SessionRuntimeProfile snapshot. The UI will not reconstruct runtime state from other fields.',
    },
    partialNotices: [...partialNotices(sources.errors), ...session.partialWarnings],
  };
}

export function buildRuntimeAuditDetailModel(sources: RuntimeAuditDetailSources): RuntimeAuditDetailModel {
  const events = getArray(asRecord(sources.audit).events);
  const selected = events.find(event => stringValue(asRecord(event).id) === sources.eventId);
  return {
    event: selected ? auditRow(selected) : null,
    rawJson: selected ? redactedJson(selected) : redactedJson(sources),
    emptyState: selected ? null : {
      title: 'Audit event not found.',
      description: 'The current /api/v1/audit page did not include this event. A dedicated audit detail endpoint would remove this lookup limit.',
    },
    partialNotices: partialNotices(sources.errors),
  };
}

export function runtimeContractGaps() {
  return [
    'Backend mutation endpoints for pause/resume/skip do not enforce explicit confirmation; Console v2 requires preview, reason, and typed confirmation before sending the mutation.',
    'Pause/resume backend metadata still marks reason optional; Console v2 treats reason as required operational evidence.',
    'Controlled test message has no dedicated Runtime preview/audit endpoint; Console v2 performs a local target preview before calling /personas/{persona}/send.',
    'SSE event stream is summary-only; payload/artifact details are fetched from /api/v1/events timeline rows.',
    'Runtime audit has no direct /api/v1/audit/{id} lookup; detail is resolved from the current audit page.',
    'Credential reveal-token support is not exposed in Runtime contracts yet; secret/token/credential fields remain redacted.',
  ];
}

export function engagementRow(value: unknown): RuntimeEngagementRow {
  const row = asRecord(value);
  const id = stringValue(row.id) || stringValue(row.engagement_id);
  const state = stringValue(row.state) || stringValue(row.canonical_state);
  const paused = boolValue(row.paused);
  const liveSessions = numberValue(row.live_sessions);
  const base = {
    id,
    personaName: stringValue(row.persona_name),
    platform: stringValue(row.platform),
    sessionKey: stringValue(row.session_key),
    state,
    paused,
    sessionsCount: numberValue(row.sessions_count),
    liveSessions,
    createdAt: stringValue(row.created_at),
    updatedAt: stringValue(row.updated_at),
    detailHref: `#/runtime/engagements/${encodeURIComponent(id)}`,
  };
  return { ...base, health: deriveEngagementHealth(base) };
}

export function deriveEngagementHealth(value: Pick<RuntimeEngagementRow, 'state' | 'paused' | 'liveSessions'>): RuntimeHealth {
  if (value.paused) return { key: 'paused', label: 'Paused', tone: 'yellow' };
  if (value.state === 'failed') return { key: 'failed', label: 'Failed', tone: 'red' };
  if (value.state === 'released') return { key: 'released', label: 'Released', tone: 'green' };
  if (value.liveSessions > 0 || value.state.includes('in-progress') || value.state.includes('review')) {
    return { key: 'active', label: 'Active', tone: 'blue' };
  }
  if (value.state) return { key: 'pending', label: 'Pending', tone: 'muted' };
  return { key: 'unknown', label: 'Unknown', tone: 'muted' };
}

function relationshipGroups(value: unknown): RuntimeRelationshipGroup[] {
  const record = asRecord(value);
  return [
    { label: 'Parents', rows: getArray(record.parents).map(relatedEngagementRow) },
    { label: 'Children', rows: getArray(record.children).map(relatedEngagementRow) },
    { label: 'Siblings', rows: getArray(record.siblings).map(relatedEngagementRow) },
  ];
}

function relatedEngagementRow(value: unknown): RuntimeRelatedEngagementRow {
  const row = asRecord(value);
  const id = stringValue(row.id);
  return {
    id,
    relation: stringValue(row.relation),
    personaName: stringValue(row.persona_name),
    platform: stringValue(row.platform),
    sessionKey: stringValue(row.session_key),
    state: stringValue(row.state),
    detailHref: `#/runtime/engagements/${encodeURIComponent(id)}`,
  };
}

function actionStatus(value: unknown): RuntimeActionStatus {
  const row = asRecord(value);
  return {
    action: stringValue(row.action),
    allowed: boolValue(row.allowed),
    code: stringValue(row.code),
    message: stringValue(row.message),
    currentState: stringValue(row.current_state),
    targetState: stringValue(row.target_state),
    currentAxes: axesTableRows(asRecord(row.current_axes)),
    targetAxes: axesTableRows(asRecord(row.target_axes)),
    requiredFields: getArray(row.required_fields).map(stringValue).filter(Boolean),
    optionalFields: getArray(row.optional_fields).map(stringValue).filter(Boolean),
    confirmationRequired: boolValue(row.confirmation_required),
    wouldRecordEvent: boolValue(row.would_record_event),
  };
}

export function eventRow(value: unknown): RuntimeEventRow {
  const row = asRecord(value);
  const payload = redactForDisplay(asRecord(row.payload)) as Record<string, unknown>;
  return {
    id: stringValue(row.event_id) || stringValue(row.id),
    type: stringValue(row.event_type),
    timestamp: stringValue(row.created_at),
    actor: stringValue(row.actor),
    personaId: stringValue(row.persona_id),
    workflowId: stringValue(row.workflow_id) || stringValue(payload.workflow_id),
    round: stringValue(row.round) || stringValue(payload.round),
    fromPersona: stringValue(row.from_persona) || stringValue(payload.from_persona),
    toPersona: stringValue(row.to_persona) || stringValue(payload.to_persona),
    intent: stringValue(row.intent) || stringValue(payload.intent),
    result: stringValue(row.result) || stringValue(payload.result),
    parentEventId: stringValue(row.parent_event_id) || stringValue(payload.parent_event_id),
    engagementId: stringValue(row.engagement_id),
    sessionId: stringValue(row.session_id),
    stateChange: stateChange(row),
    payloadFields: payloadFields(payload),
    artifactRefs: artifactRefs(payload.artifacts),
    rawJson: redactedJson(row),
  };
}

function loopVerdictSummary(value: unknown): RuntimeLoopVerdictSummary {
  const row = asRecord(value);
  const mechanics = asRecord(row.mechanics_gate);
  const contract = asRecord(row.contract_gate);
  const sentinel = asRecord(row.sentinel_gate);
  const releaseOwner = asRecord(row.release_owner_gate);
  const overall = stringValue(row.overall);
  const available = Boolean(overall || row.workflow_id || row.contract);
  const missingEvents = getArray(row.missing_events).map(stringValue).filter(Boolean);
  const invalidEvents = getArray(row.invalid_events).map(stringValue).filter(Boolean);
  const gates: RuntimeLoopVerdictGate[] = [
    verdictGate('Contract', contract),
    verdictGate('Mechanics', mechanics),
    verdictGate('Sentinel', sentinel),
    verdictGate('Release owner', releaseOwner),
  ];
  return {
    available,
    overall,
    overallTone: verdictTone(overall),
    workflowId: stringValue(row.workflow_id),
    issueId: stringValue(row.issue_id),
    currentRound: stringValue(row.current_round),
    gates,
    missingEvents,
    invalidEvents,
    fallbackUsed: boolValue(row.fallback_used),
    manualMirrorDetected: boolValue(row.manual_mirror_detected) || boolValue(row.manual_mirror_seen),
    nextAction: stringValue(row.next_action),
    rawJson: available ? redactedJson(row) : '',
  };
}

function verdictGate(label: string, row: Record<string, unknown>): RuntimeLoopVerdictGate {
  const status = stringValue(row.status) || 'unavailable';
  return {
    label,
    status,
    reason: stringValue(row.reason),
    eventId: stringValue(row.event_id),
    tone: verdictTone(status),
  };
}

function verdictTone(status: string): RuntimeStatusTone {
  switch (status) {
    case 'passed':
    case 'approved':
      return 'green';
    case 'reviewing':
      return 'blue';
    case 'degraded':
    case 'missing-events':
    case 'changes-requested':
      return 'yellow';
    case 'invalid':
    case 'invalid-events':
    case 'blocked':
    case 'not_passed':
      return 'red';
    case 'pending':
    case 'missing':
    case 'unavailable':
    default:
      return 'muted';
  }
}

function payloadFields(payload: Record<string, unknown>): RuntimeEventField[] {
  return Object.entries(payload)
    .filter(([key]) => key !== 'artifacts')
    .map(([key, value]) => ({ key, value: displayValue(value) }));
}

function artifactRefs(value: unknown): RuntimeArtifactRef[] {
  return getArray(value).map(item => {
    const record = asRecord(item);
    return { kind: stringValue(record.kind), ref: stringValue(record.ref) };
  });
}

function stateChange(row: Record<string, unknown>) {
  const from = stringValue(row.state_from);
  const to = stringValue(row.state_to);
  if (from || to) return `${from || '∅'} → ${to || '∅'}`;
  return '—';
}

function axesTableRows(value: Record<string, unknown>): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  for (const key of ['lifecycle', 'control_mode', 'reviewer_presence']) {
    const display = stringValue(value[key]);
    if (display) rows.push([formatLabel(key), display]);
  }
  return rows;
}

function sessionRow(value: unknown): RuntimeSessionRow {
  const row = asRecord(value);
  const profile = asRecord(row.runtime_profile);
  const provider = stringValue(profile.provider);
  const model = stringValue(profile.model);
  const id = stringValue(row.id);
  const runtimeProfileId = stringValue(profile.id) || stringValue(row.runtime_profile_id);
  const runtimeStatus = stringValue(profile.status) || 'missing';
  const promptStrategy = stringValue(profile.prompt_injection_strategy);
  const promptArtifacts = getArray(profile.prompt_artifacts);
  const partialWarnings = partialSessionWarnings(row, profile);
  const agentSessionId = stringValue(row.agent_session_id);

  return {
    id,
    name: stringValue(row.name) || id || 'Unnamed session',
    personaName: stringValue(row.persona_name),
    platform: stringValue(row.platform),
    sessionKey: stringValue(row.session_key),
    active: boolValue(row.active),
    live: boolValue(row.live),
    historyCount: numberValue(row.history_count),
    runtimeProfileId,
    runtimeStatus,
    providerModel: provider && model ? `${provider} / ${model}` : provider || model || 'Unknown provider/model',
    threadId: stringValue(profile.codex_thread_id) || agentSessionId,
    agentSessionId,
    personaSnapshotId: stringValue(profile.persona_snapshot_id),
    platformId: stringValue(profile.platform_id),
    promptArtifactCount: promptArtifacts.length,
    promptInjectionStrategy: promptStrategy,
    developerInstructionsHash: stringValue(profile.effective_developer_instructions_hash),
    developerPromptArtifact: stringValue(profile.effective_developer_prompt_artifact),
    promptEvidenceNote: promptEvidenceNote(promptStrategy),
    detailHref: `#/runtime/sessions/${encodeURIComponent(id)}`,
    profileHref: `#/runtime/sessions/${encodeURIComponent(id)}/profile`,
    partialWarnings,
  };
}

// promptEvidenceNote explains an absent developer-instructions hash when the
// absence is correct. The hash describes BuildDeveloperInstructions bytes; an
// adapter that takes the prompt through SetSystemPrompt receives different
// bytes, and an adapter with no channel receives none, so for those two the
// engine deliberately records no hash. Rendering that as "evidence missing"
// sends an operator hunting an API bug instead of telling them the fact.
function promptEvidenceNote(strategy: string): string {
  switch (strategy) {
    case 'system_prompt':
      return 'No developer-instructions hash by design: this adapter takes the prompt through SetSystemPrompt.';
    case 'none':
      return 'No prompt injection channel: the runtime contract does not reach this agent.';
    default:
      return '';
  }
}

// hasPromptChannel reports whether a developer-instructions hash is something
// this session could have had in the first place.
function hasPromptChannel(strategy: string): boolean {
  return strategy !== 'system_prompt' && strategy !== 'none';
}

function partialSessionWarnings(row: Record<string, unknown>, profile: Record<string, unknown>) {
  const warnings: string[] = [];
  if (!row.runtime_profile) warnings.push('SessionRuntimeProfile missing from API response.');
  if (row.runtime_profile && !profile.id) warnings.push('Runtime profile id missing.');
  if (row.runtime_profile && !profile.status) warnings.push('Runtime current/behind status missing.');
  if (row.runtime_profile
    && hasPromptChannel(stringValue(profile.prompt_injection_strategy))
    && !profile.effective_developer_instructions_hash
    && !profile.effective_developer_prompt_artifact) {
    warnings.push('Prompt hash/artifact evidence missing.');
  }
  if (row.runtime_profile && !profile.codex_thread_id && !row.agent_session_id) warnings.push('Thread id unavailable.');
  return warnings;
}

function auditRow(value: unknown): RuntimeAuditRow {
  const row = asRecord(value);
  const resource = asRecord(row.resource);
  const id = stringValue(row.id);
  const resourceKind = stringValue(resource.kind);
  const resourceId = stringValue(resource.id);
  const resourceName = stringValue(resource.name);
  return {
    id,
    timestamp: stringValue(row.timestamp),
    actor: stringValue(row.actor) || 'unknown actor',
    action: stringValue(row.action) || 'unknown action',
    resourceKind,
    resourceId,
    resourceName,
    resourceLabel: resourceKind || resourceName || resourceId
      ? `${resourceKind || 'resource'} / ${resourceName || resourceId || 'unknown'}`
      : 'unknown resource',
    detailHref: `#/runtime/audit/${encodeURIComponent(id)}`,
  };
}

function relationshipRows(sessionRecord: Record<string, unknown>, profile: Record<string, unknown>): Array<[string, string]> {
  const session = sessionRow(sessionRecord);
  return [
    ['Persona', session.personaName || stringValue(profile.persona_id) || 'Unknown'],
    ['Platform', session.platform || stringValue(profile.platform_id) || 'Unknown'],
    ['Session', session.id || 'Unknown'],
    ['RuntimeProfile', session.runtimeProfileId || 'Missing'],
    ['PersonaSnapshot', session.personaSnapshotId || 'Missing'],
    ['Provider / model', session.providerModel],
    ['Thread', session.threadId || session.agentSessionId || 'Unavailable'],
    ['Prompt hash', session.developerInstructionsHash || session.developerPromptArtifact || session.promptEvidenceNote || 'Missing'],
    ['Prompt channel', session.promptInjectionStrategy || 'Unknown'],
  ];
}

function promptArtifactRow(value: unknown): RuntimePromptArtifactRow {
  const row = asRecord(value);
  return {
    artifactId: stringValue(row.artifact_id),
    name: stringValue(row.name),
    version: numberValue(row.version),
    hash: stringValue(row.hash),
  };
}

function historyRow(value: unknown): RuntimeHistoryRow {
  const row = asRecord(value);
  const redacted = redactForDisplay(stringValue(row.content), 'message');
  return {
    role: stringValue(row.role) || 'unknown',
    timestamp: stringValue(row.timestamp),
    contentPreview: typeof redacted === 'string' ? redacted : String(redacted),
  };
}

function partialNotices(errors?: Record<string, string>) {
  return Object.entries(errors ?? {})
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}: ${value}`);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function getArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function stringValue(value: unknown) {
  return typeof value === 'string' ? value : value === undefined || value === null ? '' : String(value);
}

function numberValue(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function boolValue(value: unknown) {
  return value === true;
}

function displayValue(value: unknown) {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value, null, 2);
}

function formatLabel(value: string) {
  return value.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}
